import { useCallback, useEffect, useRef } from "react";

import debounce from "lodash/debounce";

import { FFT_SIZE, MIC_THRESHOLD } from "features/medical-scribe/utils";

interface UseAudioAnalyzerOptions {
  onVolumeChange: (volume: number, isMicActive: boolean) => void;
  /**
   * Debounce delay in ms applied to each volume callback.
   *
   * 0 (default) — calls the analyzer inline in the RAF tick for minimum latency.
   *   Best for visualization: no timer overhead, updates fire synchronously every frame.
   *
   * > 0 — debounces callbacks, useful when re-renders are expensive (e.g. Redux dispatch).
   *   Note: values < the RAF interval (~16 ms) never coalesce calls; they only add latency.
   *
   * This value is read once at mount; dynamic changes are not reflected.
   */
  debounceMs?: number;
}

/**
 * Reusable hook that drives the WaveVolumeIndicator during microphone analysis.
 *
 * Ownership contract:
 *  - Caller owns the MediaStream (getUserMedia + track cleanup).
 *  - This hook owns the AudioContext, AnalyserNode, and RAF loop.
 *
 * Usage:
 *   const { startAnalysis, stopAnalysis } = useAudioAnalyzer({
 *     onVolumeChange: (volume, isMicActive) => ...,
 *     debounceMs: 0, // default — fastest, inline in RAF tick
 *   });
 *   await startAnalysis(stream); // start
 *   stopAnalysis();              // stop
 */
export function useAudioAnalyzer({
  onVolumeChange,
  debounceMs = 0
}: UseAudioAnalyzerOptions): {
  startAnalysis: (stream: MediaStream) => Promise<void>;
  stopAnalysis: () => void;
} {
  // Always call the latest version of the callback without recreating stable refs
  const onVolumeChangeRef = useRef(onVolumeChange);
  useEffect(() => {
    onVolumeChangeRef.current = onVolumeChange;
  });

  // Capture debounceMs once at mount — this intentionally does not react to changes
  const debounceDelayRef = useRef(debounceMs);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Float32Array | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const rmsHistoryRef = useRef<number[]>([]);
  const isRunningRef = useRef(false);

  // Raw analyze function — called inline when debounceMs === 0 (fastest path)
  const analyzeRef = useRef(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    let dataArray = dataArrayRef.current;
    if (!dataArray || dataArray.length !== analyser.fftSize) {
      dataArray = new Float32Array(analyser.fftSize);
      dataArrayRef.current = dataArray;
    }
    analyser.getFloatTimeDomainData(dataArray);

    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sum / dataArray.length);

    // 60-frame rolling window (~1 second at 60fps) for stable isMicActive detection
    rmsHistoryRef.current.push(rms);
    if (rmsHistoryRef.current.length > 60) {
      rmsHistoryRef.current.shift();
    }

    const avgRms =
      rmsHistoryRef.current.reduce((a, b) => a + b, 0) /
      rmsHistoryRef.current.length;

    const volume = Math.min(100, Math.max(0, rms * 1000));
    const isMicActive = avgRms > MIC_THRESHOLD;

    onVolumeChangeRef.current(volume, isMicActive);
  });

  // Debounced wrapper — created once, only used when debounceMs > 0
  const debouncedAnalyzeRef = useRef(
    debounce(() => analyzeRef.current(), debounceDelayRef.current)
  );

  const stopAnalysis = useCallback(() => {
    isRunningRef.current = false;

    if (debounceDelayRef.current > 0) {
      debouncedAnalyzeRef.current.cancel();
    }

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    analyserRef.current = null;
    dataArrayRef.current = null;
    rmsHistoryRef.current = [];

    audioContextRef.current?.close();
    audioContextRef.current = null;
  }, []);

  const startAnalysis = useCallback(
    async (stream: MediaStream) => {
      stopAnalysis();

      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      isRunningRef.current = true;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      // Guard: stopAnalysis() may have been called while ctx.resume() was awaited.
      // Check ctx.state before closing — stopAnalysis() may have already closed it.
      if (!isRunningRef.current) {
        if (ctx.state !== "closed") ctx.close();
        return;
      }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyserRef.current = analyser;

      ctx.createMediaStreamSource(stream).connect(analyser);

      const useDebounce = debounceDelayRef.current > 0;
      const tick = () => {
        if (!isRunningRef.current) return;
        if (useDebounce) {
          debouncedAnalyzeRef.current();
        } else {
          analyzeRef.current();
        }
        rafIdRef.current = requestAnimationFrame(tick);
      };

      rafIdRef.current = requestAnimationFrame(tick);
    },
    [stopAnalysis]
  );

  // Cleanup on unmount in case the caller didn't call stopAnalysis
  useEffect(() => () => stopAnalysis(), [stopAnalysis]);

  return { startAnalysis, stopAnalysis };
}
